import React, { useState } from 'react';
import { Card, Form, Button, Alert, Container, Row, Col } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { resetPassword } from '../../services/auth';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { token, email } = useParams();
  const [resetStatus, setResetStatus] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  // We don't redirect authenticated users to allow password reset even with active session

  // Validation schema
  const validationSchema = Yup.object({
    password: Yup.string()
      .min(6, 'Password must be at least 6 characters')
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Confirm password is required')
  });

  // Initial form values
  const initialValues = {
    password: '',
    confirmPassword: ''
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setResetStatus('');
    setResetMessage('');

    try {
      await resetPassword(email, token, values.password);
      setResetStatus('success');
      setResetMessage('Your password has been reset successfully. You can now log in with your new password.');

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 3000);
    } catch (error) {
      console.error('Password reset error:', error);
      setResetStatus('error');

      if (error.errors && error.errors.password) {
        setResetMessage(error.errors.password[0]);
      } else if (error.errors && error.errors.token) {
        setResetMessage('Invalid or expired reset link. Please request a new password reset.');
      } else {
        setResetMessage('An error occurred while resetting your password. Please try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // If token or email is missing, show error
  if (!token || !email) {
    return (
      <Container>
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="shadow-sm border-0 my-5">
              <Card.Body className="p-4 text-center">
                <Alert variant="danger">
                  Invalid password reset link. Please request a new password reset.
                </Alert>
                <Link to="/forgot-password" className="btn btn-primary">
                  Request New Reset Link
                </Link>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container>
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow-sm border-0 my-5">
            <Card.Body className="p-4">
              <div className="text-center mb-4">
                <h2 className="fw-bold">Reset Password</h2>
                <p className="text-muted">
                  Enter your new password below.
                </p>
              </div>

              {resetStatus === 'success' && (
                <Alert variant="success" className="mb-4">
                  {resetMessage}
                </Alert>
              )}

              {resetStatus === 'error' && (
                <Alert variant="danger" className="mb-4">
                  {resetMessage}
                </Alert>
              )}

              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting
                }) => (
                  <Form noValidate onSubmit={handleSubmit}>
                    <Form.Group className="mb-4">
                      <Form.Label>New Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.password && errors.password}
                        placeholder="Enter your new password"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.password}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label>Confirm New Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="confirmPassword"
                        value={values.confirmPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.confirmPassword && errors.confirmPassword}
                        placeholder="Confirm your new password"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.confirmPassword}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100 py-2 mb-3"
                      disabled={isSubmitting || resetStatus === 'success'}
                    >
                      {isSubmitting ? 'Resetting...' : 'Reset Password'}
                    </Button>

                    <div className="text-center">
                      <Link to="/login" className="text-decoration-none">
                        Back to Login
                      </Link>
                    </div>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ResetPassword;
